import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { Button } from './button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { useI18n } from '@/i18n'

interface PaginationProps {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  pageSizeOptions: number[]
  compact?: boolean
}

export function Pagination({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  compact = false,
}: PaginationProps) {
  const i18n = useI18n()
  // Note: page is 0-based (matching Spring Data Page)
  const start = totalItems === 0 ? 0 : page * pageSize + 1
  const end = Math.min((page + 1) * pageSize, totalItems)

  return (
    <div className={compact
      ? "flex flex-col gap-2 px-2 py-3"
      : "flex flex-wrap items-center justify-between gap-2 px-2 py-4"
    }>
      <div className={compact
        ? "flex items-center justify-center gap-2 w-full text-sm text-muted-foreground"
        : "flex items-center gap-2 text-sm text-muted-foreground"
      }>
        <span data-testid="pagination-info" data-help-anchor="true">
          {i18n.common.showingRange({ start, end, total: totalItems })}
        </span>
        <span className="mx-2">|</span>
        <span className="flex items-center gap-2">
          <span>{i18n.common.rowsPerPage}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]" data-testid="select-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </span>
      </div>

      <div className={compact ? "flex items-center justify-center gap-1" : "flex items-center gap-1"}>
        <Button
          data-testid="btn-first-page"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(0)}
          disabled={page <= 0}
          aria-label={i18n.common.firstPage}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          data-testid="btn-previous-page"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 0}
          aria-label={i18n.common.previousPage}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-3 text-sm" data-testid="pagination-page-info">
          {i18n.common.pageOf({ page: page + 1, totalPages: totalPages || 1 })}
        </span>
        <Button
          data-testid="btn-next-page"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          aria-label={i18n.common.nextPage}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          data-testid="btn-last-page"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(totalPages - 1)}
          disabled={page >= totalPages - 1}
          aria-label={i18n.common.lastPage}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
